import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  IconButton,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { athleteDisplayName } from '@strength/domain';
import {
  MdExpandMore,
  MdPlaylistAdd,
  MdPlaylistRemove,
} from '@teamworksdev/icons';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type AthleteOption = { id: string; givenName: string; familyName: string };

function AthleteRow({
  athlete,
  isSelected,
  onToggle,
}: {
  athlete: AthleteOption;
  isSelected: boolean;
  onToggle: (athleteId: string, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const name = athleteDisplayName(athlete);
  return (
    <div className="flex flex-row justify-between m-3">
      <Typography variant="body2">{name}</Typography>
      <Checkbox
        sx={{ m: 0, p: 0 }}
        checked={isSelected}
        onChange={(e) => onToggle(athlete.id, e.target.checked)}
        aria-label={
          isSelected
            ? t('unselectAthlete', { name })
            : t('selectAthlete', { name })
        }
      />
    </div>
  );
}

// Tracks can have 4,000+ athletes, each rendered as a row in the sidebar.
// Without memo, toggling one checkbox re-renders every row. memo skips
// re-rendering rows whose props (athlete, isSelected, onToggle) haven't changed.
// TODO: virtualizing this list would also solve this by limiting the number of rows rendered at once.
const MemoizedAthleteRow = memo(AthleteRow);

export function AthleteStatusAccordion({
  status,
  athletes,
  selectedAthleteIdSet,
  onToggleAthlete,
  onSelectAll,
  onDeselectAll,
}: {
  status: 'planned' | 'completed';
  athletes: AthleteOption[];
  selectedAthleteIdSet: ReadonlySet<string>;
  onToggleAthlete: (athleteId: string, checked: boolean) => void;
  onSelectAll: (athleteIds: string[]) => void;
  onDeselectAll: (athleteIds: string[]) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  if (athletes.length === 0) {
    return null;
  }

  const athleteIds = athletes.map((a) => a.id);
  const selectedCount = athletes.filter((a) =>
    selectedAthleteIdSet.has(a.id),
  ).length;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      // Disable accordion transition because performance is bad with long athlete lists
      // TODO: virtualizing this list would also solve this by limiting the number of rows rendered at once.
      slotProps={{ transition: { timeout: 0 } }}
      disableGutters
      sx={{
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        className="p-0 flex-row-reverse gap-1"
        sx={{
          '.MuiAccordionSummary-content': {
            margin: 0,
          },
          borderBottom: '1px',
          borderStyle: 'solid',
          borderColor: grey[200],
          minHeight: `28px`,
        }}
        expandIcon={<MdExpandMore size={20} />}
      >
        <div className="flex flex-row justify-between items-center w-full">
          <Typography variant="body2">{t(status)}</Typography>
          <IconButton
            aria-label={selectedCount === 0 ? t('selectAll') : t('deselectAll')}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedCount === 0) {
                onSelectAll(athleteIds);
              } else {
                onDeselectAll(athleteIds);
              }
            }}
          >
            {selectedCount === 0 ? (
              <MdPlaylistAdd size={20} aria-hidden />
            ) : (
              <MdPlaylistRemove size={20} aria-hidden />
            )}
          </IconButton>
        </div>
      </AccordionSummary>
      <AccordionDetails className="p-0">
        {athletes.map((athlete) => (
          <MemoizedAthleteRow
            key={athlete.id}
            athlete={athlete}
            isSelected={selectedAthleteIdSet.has(athlete.id)}
            onToggle={onToggleAthlete}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
